import { alpha, Avatar, Box, Grid, Stack, Typography, TableRow, TableCell, useTheme } from '@mui/material'
import { LOGOS_BASE_URL } from '../../assets/constants'
import { blue, green, red } from '../../assets/colors'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getColorForSymbol, toLocaleStringIn } from '../../util/general'
import ThinArrowUpIcon from '../../assets/icons/ThinArrowUpIcon'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import { handleOverviewSecurityClick } from './SecurityOverviewHeader'
import { useDispatch, useSelector } from 'react-redux'
import { selectSnapshot } from '../../selectors/security'
import { calculateSnapshotDetails, getSecurityAsync, getSecurityKey } from '../../util/security'
import { ExtraSmallButtonHoverable, InteractiveTooltip, NonInteractiveTooltip } from '../custom'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import { setQuickOrderData } from '../../reducer/orders.reducer'
import CompanyEvents from '../CompanyEvents/companyEvents'
import { Broker_OrderType, OrderType, Product, TransactionType } from '../../proto/generated/order'
import OrderBookDialog from '../orderBook/OrderBookDialog'
import { exchangeToJSON } from '../../proto/generated/common'
import Chart from '../../assets/icons/Chart'
import { useStyles } from "../alerts/alertListItem/styles";
import OptionChain from '../../assets/icons/OptionChain'
import { generatePath } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { selectPrimarySecurity } from '../../selectors/home'
import { ViewType } from '../../assets/constants'
import { Security, SecurityCategory_CategoryCode } from '../../proto/generated/security'
import SecurityRange from './SecurityRange.tsx'

type Props = {
    component: IndexComponentData,
    subscribeSecurity: Function,
}

export type IndexComponentData = {
    id: string,
    security: Security,
    ltp: number | null,
    change: number | null,
    changePercentage: number | null,
    contribution: number | null,
    weightage: number | null,
    previousClose: number
}

interface LtpStats {
    ltp: number,
    ltpChange: number,
    changePercentage: number,
    contribution: number
}

const ComponentView = ({ component, subscribeSecurity }: Props) => {
    const { classes } = useStyles()
    const theme = useTheme()
    const { security: securityProp, ltp, change, changePercentage, contribution, previousClose } = component
    const [ltpStats, setLtpStats] = useState<LtpStats>({
        ltp: ltp ?? 0,
        ltpChange: change ?? 0,
        changePercentage: changePercentage ?? 0,
        contribution: contribution ?? 0
    })
    const {
        expressionMode
    } = useSelector(selectPrimarySecurity)
    const [view, setView] = useState<ViewType>(ViewType.OPTION_CHAIN)
    const secKey = useMemo(() => getSecurityKey(securityProp.exchange, securityProp.code), [securityProp])
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (secKey) getSecurityAsync(secKey).then(setSecurity) }, [secKey])
    const securitySnapshot = useSelector(selectSnapshot(secKey))
    const dispatch = useDispatch()
    const [expanded, setExpanded] = useState(false)
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const tooltipOpenTimeoutId = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (securitySnapshot && security) {
            const previousLtpStats = { ...ltpStats }
            const data = calculateSnapshotDetails(security, {
                ...securitySnapshot,
                previousClose
            }) || previousLtpStats
            let newLtpStats: LtpStats = {
                ...ltpStats,
                ltp: data.ltp,
                ltpChange: data.ltpChange,
                changePercentage: data.changePercentage
            }
            if (newLtpStats.ltp !== previousLtpStats.ltp) {
                setLtpStats(newLtpStats)
            }
        }
    }, [securitySnapshot, security])

    const handleComponentClick = useCallback(() => {
        handleOverviewSecurityClick(security)
    }, [security])

    const displayName = useMemo(() => security?.displayName, [security?.displayName])
    const logoUrl = useMemo(() => security?.logoUrl, [security?.logoUrl])
    const getPathForOptionChain = useCallback((): string => {
        return generatePath(ROUTES.optionChain, {
            exchange: exchangeToJSON(security?.exchange ?? 1),
            symbol: security?.code
        });
    }, [security]);

    const handleOptionChainClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            AnalyticsManager.logEvent('Navigation Button Clicked', {
                source: 'watchlist',
                buttonType: ViewType.OPTION_CHAIN,
            });
            if (!security || expressionMode) return;
            if (view === ViewType.OPTION_CHAIN) {
                window.open(getPathForOptionChain(), '_blank');
            }
        },
        [security, expressionMode, view, getPathForOptionChain]
    );

    const title = useMemo(() => {
        return (
            <Grid container direction="column"
                  sx={{borderRadius: '4px', minWidth: '150px', width: 'auto', height: 'auto' }}>
                <>
                <Grid item>
                    <Typography
                        variant="body2">{security?.displayName} | {exchangeToJSON(security?.exchange)}</Typography>
                </Grid>
                </>
            </Grid>
        )
    }, [classes.tooltip, security?.displayName, security?.exchange])

    useEffect(() => {
        setTooltipOpen(false);
    }, [popoverOpen]);

    const symbolDisplay = useMemo(() => {
        const symbolOrCode = security?.symbol || security?.code || ''
        return (
            <Grid item xs={4}>
                <Stack direction={'row'} spacing={1} alignItems={'center'}>
                    {logoUrl ?
                        <Avatar alt={displayName} src={`${LOGOS_BASE_URL}${logoUrl}.svg`}
                            sx={{ width: 20, height: 20 }} /> :
                        <Avatar alt={'Security Logo'}
                            sx={{
                                width: 20,
                                height: 20,
                                fontSize: 12,
                                color: 'white',
                                bgcolor: getColorForSymbol(symbolOrCode, theme.palette.background.default)
                            }}>{symbolOrCode.split(' ').map((word) => word[0]?.toUpperCase()).slice(0, 2).join('')}</Avatar>
                    }
                    <Typography noWrap variant={'body2'} sx={{ lineHeight: '24px' }}>
                        {security?.symbol}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CompanyEvents companyCode={security?.companyCode || ''} onPopoverOpenChange={setPopoverOpen} />
                    </Box>
                </Stack>
            </Grid>
        )
    }, [displayName, handleComponentClick, logoUrl, security?.code, security?.symbol, security?.companyCode, theme.palette.background.default])

    const handleQuickTrade = useCallback((transaction: TransactionType, orderType: OrderType = OrderType.NORMAL, brokerOrderType: Broker_OrderType = Broker_OrderType.LIMIT_ORDER, product: Product = Product.CNC, price: number | undefined = undefined) => {
        if (security) {
            AnalyticsManager.logEvent(`${transaction === TransactionType.BUY ? 'Buy' : 'Sell'} Clicked`, {
                source: 'chart-grid',
                symbol: security.symbol
            })
            let quantity = security.lotSize ?? 1
            dispatch(setQuickOrderData({
                openDialog: true,
                mode: orderType === OrderType.TRIGGER ? 'trigger' : 'regular',
                type: 'trade',
                isSquareOffOrder: false,
                order: {
                    transactionType: transaction,
                    security: security,
                    editOrder: undefined,
                    brokerOrderType: brokerOrderType,
                    orderType,
                    product: product,
                    price: price,
                    triggerPrice: orderType === OrderType.TRIGGER ? price : undefined,
                    quantity,
                    maxQuantity: 0
                }
            }))
        }
    }, [dispatch, security])

    const handleExpandClick = useCallback(() => {
        if (security) {
            subscribeSecurity(
                security,
                !expanded
            )
            setExpanded(!expanded)
        }
    }, [expanded, subscribeSecurity, security])

    const handleChartClick = useCallback(() => {
        security && handleOverviewSecurityClick(security)
    }, [security])

    const actionButtons = useMemo(() => (
        <Stack
            spacing={0.5}
            direction={'row'}
            className={'action-btns'}
            sx={{
                position: 'absolute',
                right: 2,
                zIndex: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '28px',
                visibility: 'hidden',
                '&:hover': {
                    visibility: 'visible'
                }
            }}
        >
            {security?.hasFo && <NonInteractiveTooltip placement="top"
                arrow={true}
                title={<Typography variant={'caption'}
                    sx={{
                        maxWidth: 'none',
                        whiteSpace: 'nowrap'
                    }}>{'Option Chain'}</Typography>}>
                <ExtraSmallButtonHoverable variant="outlined"
                    color="inherit"
                    sx={{ maxWidth: '32px', padding: 0 }} onClick={handleOptionChainClick}>

                    <OptionChain sx={{ fontSize: "28px" }} />
                </ExtraSmallButtonHoverable>

            </NonInteractiveTooltip>}
            <NonInteractiveTooltip placement="top"
                arrow={true}
                title={<Typography variant={'caption'}
                    sx={{
                        maxWidth: 'none',
                        whiteSpace: 'nowrap'
                    }}>{'Charts'}</Typography>}>
                <ExtraSmallButtonHoverable
                    variant="outlined"
                    color="inherit"
                    sx={{ maxWidth: '32px', padding: 0 }}
                    onClick={handleChartClick}
                >
                    <Chart sx={{ fontSize: "28px" }} />
                </ExtraSmallButtonHoverable>
            </NonInteractiveTooltip>
            {
                security?.categoryCode !== SecurityCategory_CategoryCode.INDEX && (
                    <>
                        <NonInteractiveTooltip placement="top"
                            arrow={true}
                            title={<Typography variant={'caption'}
                                sx={{
                                    maxWidth: 'none',
                                    whiteSpace: 'nowrap'
                                }}>{'Buy'}</Typography>}>
                            <ExtraSmallButtonHoverable
                                variant={'outlined'}
                                color={'inherit'}
                                sx={{
                                   height:"28px", width:'28px', padding:0,
                                    backgroundColor: alpha(blue, 0.8),
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: blue
                                    },
                                    '&:active': {
                                        backgroundColor: blue
                                    }
                                }}
                                onClick={(
                                    clickEvent: React.MouseEvent<HTMLSpanElement>
                                ) => {
                                    clickEvent.stopPropagation()
                                    handleQuickTrade(TransactionType.BUY)
                                }}
                            >
                                B
                            </ExtraSmallButtonHoverable>
                        </NonInteractiveTooltip>
                        <NonInteractiveTooltip placement="top"
                            arrow={true}
                            title={<Typography variant={'caption'}
                                sx={{
                                    maxWidth: 'none',
                                    whiteSpace: 'nowrap'
                                }}>{'Sell'}</Typography>}>
                            <ExtraSmallButtonHoverable
                                variant={'outlined'}
                                color={'inherit'}
                                sx={{
                                    height:"28px", width:'28px', padding:0,
                                    background: alpha(red, 0.8),
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: red
                                    },
                                    '&:active': {
                                        backgroundColor: red
                                    }
                                }}
                                onClick={(
                                    clickEvent: React.MouseEvent<HTMLSpanElement>
                                ) => {
                                    clickEvent.stopPropagation()
                                    handleQuickTrade(TransactionType.SELL)
                                }}
                            >
                                S
                            </ExtraSmallButtonHoverable>
                        </NonInteractiveTooltip>
                    </>
                )
            }
            {(<NonInteractiveTooltip placement="top"
                arrow={true}
                title={<Typography variant={'caption'}
                    sx={{
                        maxWidth: 'none',
                        whiteSpace: 'nowrap'
                    }}>{'Market Depth'}</Typography>}>
                <ExtraSmallButtonHoverable
                    variant={'outlined'}
                    color={'inherit'}
                    sx={{height:"28px", width:'28px', padding:0, }}
                    onClick={(
                        clickEvent: React.MouseEvent<HTMLSpanElement>
                    ) => {
                        clickEvent.stopPropagation()
                        handleExpandClick()
                    }}
                >
                    <FormatAlignCenterIcon sx={{ fontSize: '16px' }} />
                </ExtraSmallButtonHoverable>
            </NonInteractiveTooltip>)}
        </Stack>
    ), [blue, red, security, handleChartClick, security, handleQuickTrade, handleExpandClick])

    const ltpStatsDisplay = useMemo(() => {
        return (
            <Stack direction={'row'} alignItems={'end'} justifyContent={'flex-end'} flexGrow={1} spacing={0.5}>
                <Typography variant={'body2'} color={'textSecondary'} align={'right'} style={{ width: 80 }}>
                    {(ltpStats.contribution ?? 0).toFixed(2)} pts
                </Typography>
                <Typography variant={'body2'} color={'textSecondary'} align={'right'} style={{ width: 55 }}>
                    {ltpStats.changePercentage !== null ? ltpStats.changePercentage.toFixed(2) + '%' : '-'}
                </Typography>
                {!!(ltpStats.ltpChange && ltpStats.ltpChange >= 0) &&
                    <ThinArrowUpIcon fontSize={'small'} sx={{ color: green }} />
                }
                {!!(ltpStats.ltpChange && ltpStats.ltpChange < 0) &&
                    <ThinArrowDownIcon fontSize={'small'} sx={{ color: red }} />
                }
                {
                    !ltpStats.ltpChange &&
                    <ThinArrowDownIcon fontSize={'small'} sx={{ color: 'transparent' }} />
                }
                <Typography variant={'body2'} align={'right'} style={{
                    width: 65,
                    color: ltpStats.ltpChange && ltpStats.ltpChange >= 0 ? green : red,
                    fontFeatureSettings: '"tnum" on,"lnum" on'
                }}>
                    {ltpStats.ltp !== null ? toLocaleStringIn(ltpStats.ltp) : '-'}
                </Typography>
            </Stack>
        )
    }, [ltpStats])

    if (!security) {
        return null
    }

    return (
        <>
            <TableRow
                hover
                onClick={handleComponentClick}
                sx={{
                    '&:hover .action-btns': { visibility: 'visible' },
                    height: 36,
                    position: 'relative',
                    borderBottomColor: (theme) => theme.palette.divider
                }}
            >
                <TableCell sx={{ py: 0.5, px: 1, minWidth: 130, width: '36%', borderBottomColor: (theme) => theme.palette.divider }}>
                    <NonInteractiveTooltip placement="left" arrow title={title}>
                        <Stack direction={'row'} spacing={0.5} alignItems="center">
                            {logoUrl ? (
                                <Avatar alt={displayName} src={`${LOGOS_BASE_URL}${logoUrl}.svg`} sx={{ width: 18, height: 18, flexShrink: 0 }} />
                            ) : (
                                <Avatar
                                    alt={'Security Logo'}
                                    sx={{
                                        width: 18,
                                        height: 18,
                                        fontSize: 10,
                                        color: 'white',
                                        flexShrink: 0,
                                        bgcolor: getColorForSymbol(security.symbol || security.code, theme.palette.background.default)
                                    }}
                                >
                                    {(security.symbol || security.code)
                                        .split(' ')
                                        .map((word) => word[0]?.toUpperCase())
                                        .slice(0, 2)
                                        .join('')}
                                </Avatar>
                            )}
                            <Typography
                                noWrap
                                variant={'body2'}
                                sx={{
                                    fontSize: '0.8125rem',
                                    lineHeight: 1.2,
                                    maxWidth: 'calc(100% - 24px)',
                                    flexGrow: 1,
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {security.symbol}
                            </Typography>
                            <Box sx={{ ml: 'auto', display: 'flex', flexShrink: 0 }}>
                                <CompanyEvents companyCode={security?.companyCode || ''} onPopoverOpenChange={setPopoverOpen} />
                            </Box>
                        </Stack>
                    </NonInteractiveTooltip>
                </TableCell>

                <TableCell align="right" sx={{ py: 0.5, px: 1, width: '22%', borderBottomColor: (theme) => theme.palette.divider }}>
                    <Typography
                        variant="body2"
                        noWrap
                        align="right"
                        sx={{
                            fontSize: '0.8125rem',
                            fontFeatureSettings: '"tnum" on,"lnum" on',
                            borderBottomColor: (theme) => theme.palette.divider
                        }}
                    >
                        {(ltpStats.contribution ?? 0).toFixed(2)} pts
                    </Typography>
                </TableCell>

                <TableCell align="right" sx={{ py: 0.5, px: 1, width: '20%', borderBottomColor: (theme) => theme.palette.divider }}>
                    <Typography variant="body2" noWrap align="right" sx={{ fontSize: '0.8125rem' }}>
                        {ltpStats.changePercentage !== null ? ltpStats.changePercentage.toFixed(2) + '%' : '-'}
                    </Typography>
                </TableCell>

                <TableCell align="right" sx={{ py: 0.5, px: 1, width: '22%', position: 'relative', borderBottomColor: (theme) => theme.palette.divider }}>
                    <Typography
                        variant="body2"
                        noWrap
                        align="right"
                        sx={{
                            color: ltpStats.ltpChange && ltpStats.ltpChange >= 0 ? green : red,
                            fontFeatureSettings: '"tnum" on,"lnum" on',
                            fontSize: '0.8125rem'
                        }}
                    >
                        {ltpStats.ltp !== null ? toLocaleStringIn(ltpStats.ltp) : '-'}
                    </Typography>
                    {actionButtons}
                </TableCell>
            </TableRow>

            {expanded && security && (
                <TableRow>
                    <TableCell colSpan={4}>
                        <OrderBookDialog security={security} />
                        <SecurityRange security={security} />
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}

export default memo(ComponentView)
